import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, useFetch, Spinner } from "@/components/portal/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const STATUSES = ["all", "In Transit", "Delivered", "Pending Pickup", "Delayed", "At Risk", "Exception"];

function BookDialog({ cities, onBooked }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ origin_city: "Cape Town", dest_city: "Johannesburg", cargo_type: "General Palletised", weight_kg: 1000, dimensions: "6x2.4x2.6m", priority: "Standard", service_type: "Full Truckload (FTL)" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    try {
      const { data } = await api.post("/portal/book", { ...f, weight_kg: Number(f.weight_kg) });
      toast.success(`Shipment booked: ${data.shipment.tracking_number}`);
      setOpen(false); onBooked();
    } catch { toast.error("Booking failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-red-700" data-testid="book-open"><Plus size={16} className="mr-2" /> Book Shipment</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Book a new shipment</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Origin</Label><Select value={f.origin_city} onValueChange={(v) => set("origin_city", v)}><SelectTrigger data-testid="book-origin" className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Destination</Label><Select value={f.dest_city} onValueChange={(v) => set("dest_city", v)}><SelectTrigger data-testid="book-dest" className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          <div className="col-span-2"><Label>Cargo type</Label><Input data-testid="book-cargo" value={f.cargo_type} onChange={(e) => set("cargo_type", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Weight (kg)</Label><Input data-testid="book-weight" type="number" value={f.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Dimensions</Label><Input value={f.dimensions} onChange={(e) => set("dimensions", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Priority</Label><Select value={f.priority} onValueChange={(v) => set("priority", v)}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{["Standard", "Express", "Critical"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Service</Label><Select value={f.service_type} onValueChange={(v) => set("service_type", v)}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{["Full Truckload (FTL)", "Less-than-Truckload (LTL)", "Container Transport", "Cross-Border", "Last-Mile Distribution"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <DialogFooter><Button className="bg-primary hover:bg-red-700" onClick={submit} data-testid="book-submit">Confirm Booking</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Shipments() {
  const [status, setStatus] = useState("all");
  const nav = useNavigate();
  const { isStaff } = useAuth();
  const { data, loading, setData } = useFetch(() => api.get(`/portal/shipments?status=${status}`).then((r) => r.data), [status]);
  const cities = useFetch(() => api.get("/public/cities").then((r) => r.data.cities)).data || [];

  const reload = () => api.get(`/portal/shipments?status=${status}`).then((r) => setData(r.data));

  return (
    <div>
      <PageHeader title="Shipments" subtitle={isStaff ? "All shipments across the network" : "Track and manage your consignments"}>
        {!isStaff && <BookDialog cities={cities} onBooked={reload} />}
      </PageHeader>

      <Tabs value={status} onValueChange={setStatus} className="mb-5">
        <TabsList className="flex-wrap h-auto">
          {STATUSES.map((s) => <TabsTrigger key={s} value={s} data-testid={`filter-${s}`} className="capitalize">{s}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {loading ? <Spinner /> : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-4">Tracking #</th><th className="p-4">Route</th><th className="p-4 hidden md:table-cell">Service</th>
                  <th className="p-4 hidden lg:table-cell">Priority</th><th className="p-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.shipments.map((sh) => (
                  <tr key={sh.id} onClick={() => nav(`/app/shipments/${sh.id}`)} data-testid={`ship-row-${sh.tracking_number}`}
                    className="hover:bg-muted/40 cursor-pointer">
                    <td className="p-4 font-mono font-semibold">{sh.tracking_number}</td>
                    <td className="p-4">{sh.origin} → {sh.destination}</td>
                    <td className="p-4 hidden md:table-cell text-muted-foreground">{sh.service_type}</td>
                    <td className="p-4 hidden lg:table-cell">{sh.priority}</td>
                    <td className="p-4"><StatusBadge status={sh.status} /></td>
                  </tr>
                ))}
                {data.shipments.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No shipments found.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
